import { faClose, faUtensils } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { IPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface RodizioPlanDetailsPupopProps {
  product: IProductRequest
  theme?: IPublicMenuTheme | null
  canRequestJoin?: boolean
  onClose: () => void
  onRequestJoin: () => void
}

export const RodizioPlanDetailsPupop: React.FC<RodizioPlanDetailsPupopProps> = ({
  product,
  theme,
  canRequestJoin = true,
  onClose,
  onRequestJoin,
}) => {
  const { t } = useTranslation()

  const isLegacyTheme = !theme || isLegacyPublicMenuTheme(theme)

  const subProductNames = product.rodizioSubProductNames ?? []

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay} onClick={onClose}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section
            className={defaultStyle.alert1}
            style={isLegacyTheme || !theme ? undefined : { backgroundColor: theme.surfaceColor, color: theme.textColor }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2"
              style={isLegacyTheme || !theme ? undefined : { borderColor: theme.borderColor }}
            >
              <div className="flex min-w-0 items-center gap-2">
                <FontAwesomeIcon
                  icon={faUtensils}
                  className="shrink-0 text-amber-600"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.accentColor }}
                />

                <h2
                  className="truncate text-sm font-black uppercase tracking-wide text-gray-900"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor }}
                >
                  {t("rodizio:public.badge")}
                </h2>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label={t("publicMenu:notice.close")}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white transition hover:bg-red-700"
                style={
                  isLegacyTheme || !theme ? undefined : { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                }
                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.rodizioPlanDetailsPupop.button.onClose"
              >
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>

            <div className="flex flex-col items-center space-y-3 p-4 text-center sm:items-start sm:text-left">
              {product.image && (
                <img
                  src={product.image.toClientRequest()}
                  alt={product.name}
                  className="mx-auto h-24 w-24 rounded-lg border object-cover sm:mx-0"
                  style={isLegacyTheme || !theme ? undefined : { borderColor: theme.borderColor }}
                  loading="lazy"
                  decoding="async"
                />
              )}

              <div className="w-full">
                <h3
                  className="text-base font-black text-gray-900"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor }}
                >
                  {product.name}
                </h3>

                <p
                  className="text-lg font-black text-emerald-700"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.primaryColor }}
                >
                  R$ {product.price.toFixed(2).replace(".", ",")}
                </p>
              </div>

              {product.description && (
                <p
                  className="w-full whitespace-pre-wrap break-words text-justify text-sm text-gray-700 sm:text-left"
                  style={isLegacyTheme || !theme ? undefined : { color: theme.textColor, opacity: 0.85 }}
                >
                  {product.description}
                </p>
              )}

              {subProductNames.length > 0 && (
                <div
                  className="w-full rounded-lg bg-black/5 px-3 py-2 text-left"
                  style={isLegacyTheme || !theme ? undefined : { backgroundColor: theme.secondaryColor }}
                >
                  <p className="text-[10px] font-black uppercase tracking-[0.15em] opacity-70">
                    {t("rodizio:public.plan_includes")}
                  </p>

                  <ul className="mt-1 space-y-1">
                    {subProductNames.map((subProductName) => (
                      <li key={subProductName} className="text-sm font-semibold opacity-90">
                        🍽️ {subProductName}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p
                className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-justify text-xs font-semibold text-amber-800 sm:text-left"
                style={
                  isLegacyTheme || !theme
                    ? undefined
                    : { borderColor: theme.borderColor, backgroundColor: theme.secondaryColor, color: theme.textColor }
                }
              >
                {t("rodizio:public.menu_card_notice")}
              </p>

              {canRequestJoin && (
                <button
                  type="button"
                  onClick={onRequestJoin}
                  className="w-full rounded-lg px-4 py-3 text-sm font-black uppercase tracking-wide text-white transition hover:opacity-90"
                  style={
                    isLegacyTheme || !theme
                      ? { backgroundColor: "#DC2626" }
                      : { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                  }
                  data-interaction-description-key="interactionDescriptions:publicMenu.pupop.rodizioPlanDetailsPupop.button.onRequestJoin"
                >
                  {t("rodizio:public.enter_code")}
                </button>
              )}
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
